/**
 * TrashTab.jsx — Settings ▸ Trash (jobs:full or invoices:full).
 *
 * Every job and invoice delete is snapshotted into deleted_records in the same
 * transaction as the DELETE (server/lib/trash.js) and kept 30 days, then purged.
 * Restoring a job brings back its invoices, payout lines, time and expenses and
 * re-links quotes; restoring needs jobs:full for jobs, invoices:full for invoices.
 */
import { useMemo, useState } from 'react';
import { useTrash, useRestoreTrash } from '../../hooks/useTrash';
import { useSettings } from '../../hooks/useSettings';
import { useRBAC } from '../../hooks/useRBAC';
import { useToast } from '../layout/Toast';
import { cn, fmtC, formatStatus } from '../../lib/utils';

// deleted_records.allowed_by → why the delete was permitted. Bulk invoices
// store one reason per covered job, comma-joined.
const ALLOWED_BY = {
  created_by: 'Created it',
  assigned_crew: 'In the pay split',
  calc_snapshot: 'In the pay calculation',
  payout_ledger: 'Had a payout line',
  crew_earnings: 'Had crew earnings',
  assigned_crew_history: 'Was in the split (history)',
  'jobs:full': 'Admin',
  'invoices:full': 'Admin',
};

// Job rows' summary counts (GET /api/trash), in display order.
const COUNT_LABELS = {
  invoices: ['invoice', 'invoices'],
  job_earnings_ledger: ['payout line', 'payout lines'],
  time_entries: ['time entry', 'time entries'],
  expenses: ['expense', 'expenses'],
  gcal_sync: ['calendar event', 'calendar events'],
};

function allowedText(v) {
  if (!v) return '—';
  return [...new Set(String(v).split(',').map((r) => ALLOWED_BY[r.trim()] || r.trim()))].join(' · ');
}

/** "2 invoices · 3 payout lines" — empty when nothing went with the job. */
function containedText(summary) {
  return Object.entries(COUNT_LABELS)
    .map(([k, [one, many]]) => {
      const n = Number(summary?.[k]) || 0;
      return n > 0 ? `${n} ${n === 1 ? one : many}` : null;
    })
    .filter(Boolean)
    .join(' · ');
}

/** The label is a title / invoice number; say whose it was and what it was worth. */
function detailText(row) {
  const s = row.summary || {};
  const parts = row.tableName === 'jobs'
    ? [s.client, s.status && `was ${formatStatus(s.status)}`]
    : [s.client, s.amount != null && fmtC(s.amount), s.bulk && 'bulk'];
  return parts.filter(Boolean).join(' · ');
}

const fmtWhen = (ts) => (ts
  ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—');

// Whole days remaining, rounded down: purge_after is 30 *calendar* days out, so
// across a DST change it's 30d ± 1h and rounding up would show "31 days left".
function daysLeft(purgeAfter) {
  if (!purgeAfter) return null;
  return Math.max(0, Math.floor((new Date(purgeAfter).getTime() - Date.now()) / 86_400_000));
}

function TrashRow({ row, canRestore, jobInTrash, onRestore, restoring, notes }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const isJob = row.tableName === 'jobs';
  const restored = !!row.restoredAt;
  const left = daysLeft(row.purgeAfter);
  const detail = detailText(row);
  const contained = isJob ? containedText(row.summary) : '';

  const restore = async () => {
    setError('');
    try {
      await onRestore(row);
      setConfirming(false);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={cn('p-3 bg-surface border border-border rounded', restored && !notes?.length && 'opacity-60')}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn(
          'px-1.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide border',
          isJob ? 'text-accent border-accent/40 bg-accent/10' : 'text-purple border-purple/40 bg-purple/10',
        )}>
          {isJob ? 'Job' : row.tableName === 'invoices' ? 'Invoice' : row.tableName}
        </span>
        {/* min width so on a phone the status/Restore wrap below instead of crushing the label */}
        <span className="text-sm text-text-primary flex-1 min-w-[8rem] truncate">{row.label || row.recordId}</span>
        {restored ? (
          <span className="text-xs text-text-dim">
            Restored by {row.restoredBy || 'unknown'} · {fmtWhen(row.restoredAt)}
          </span>
        ) : (
          <span className="flex items-center gap-2 ml-auto">
            {left !== null && (
              <span className={cn('text-xs tabular-nums', left <= 3 ? 'text-red-accent' : 'text-text-dim')}>
                {left === 0 ? 'Less than a day left' : `${left} day${left === 1 ? '' : 's'} left`}
              </span>
            )}
            {canRestore ? (
              !confirming && (
                <button data-track="trash.restore" onClick={() => setConfirming(true)}
                  className="px-2.5 py-1 rounded text-xs border border-border text-text-dim hover:text-text-primary hover:border-accent">
                  Restore
                </button>
              )
            ) : (
              <span className="text-2xs text-text-dim">Needs {isJob ? 'Jobs' : 'Invoices'}: Full to restore</span>
            )}
          </span>
        )}
      </div>

      <div className="text-xs text-text-dim mt-1 space-y-0.5">
        {detail && <div>{detail}</div>}
        <div>Deleted by {row.deletedByName || 'unknown'} · {fmtWhen(row.deletedAt)} · Allowed: {allowedText(row.allowedBy)}</div>
        {isJob && <div>{contained ? `Contained: ${contained}` : 'Nothing else went with it'}</div>}
        {!restored && jobInTrash && <div>Its job is in Trash too — restore the job first.</div>}
      </div>

      {confirming && !restored && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-primary flex-1 min-w-0">
            {isJob
              ? 'Restore this job with everything that went with it? Quotes are re-linked and calendar events re-created.'
              : 'Restore this invoice? Payout lines on its job(s) are recalculated.'}
          </span>
          <button data-track="trash.restoreCancel" onClick={() => { setConfirming(false); setError(''); }}
            className="px-2.5 py-1 rounded text-xs border border-border text-text-dim hover:text-text-primary">
            Cancel
          </button>
          <button data-track="trash.restoreConfirm" onClick={restore} disabled={restoring}
            className="px-2.5 py-1 bg-accent text-bg rounded text-xs font-medium hover:bg-accent-hover disabled:opacity-50">
            {restoring ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      )}
      {error && <div className="text-xs text-red-accent mt-1.5">{error}</div>}
      {notes?.length > 0 && (
        <div className="mt-2 p-2 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-text-primary space-y-1">
          <div className="font-semibold">Restored with notes — follow up on these:</div>
          {notes.map((n) => <div key={n}>• {n}</div>)}
        </div>
      )}
    </div>
  );
}

export default function TrashTab() {
  const { data: rows = [], isLoading, isError, error, refetch } = useTrash();
  const { data: settings } = useSettings();
  const { hasAccess } = useRBAC();
  const restore = useRestoreTrash();
  const { toast } = useToast();
  // Restore warnings, kept on the row: a toast is gone in 3s, and some of them
  // ask the admin to do something (re-sync the calendar, re-enter hours).
  const [notesById, setNotesById] = useState({});

  const canJobs = hasAccess('jobs', 'full', settings);
  const canInvoices = hasAccess('invoices', 'full', settings);

  const { open, done, jobsInTrash } = useMemo(() => ({
    open: rows.filter((r) => !r.restoredAt),
    done: rows.filter((r) => r.restoredAt),
    jobsInTrash: new Set(rows.filter((r) => r.tableName === 'jobs' && !r.restoredAt).map((r) => r.recordId)),
  }), [rows]);

  const handleRestore = async (row) => {
    const result = await restore.mutateAsync(row.id);
    const notes = result?.warnings || [];
    if (notes.length) setNotesById((m) => ({ ...m, [row.id]: notes }));
    toast(`Restored ${row.tableName === 'jobs' ? 'job' : 'invoice'}${notes.length ? ' — see the notes on it below' : ''}`);
  };

  const renderRow = (row) => (
    <TrashRow
      key={row.id}
      row={row}
      canRestore={row.tableName === 'jobs' ? canJobs : row.tableName === 'invoices' ? canInvoices : false}
      jobInTrash={row.tableName === 'invoices' && !!row.jobId && jobsInTrash.has(row.jobId)}
      onRestore={handleRestore}
      restoring={restore.isPending && restore.variables === row.id}
      notes={notesById[row.id]}
    />
  );

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">Trash</h3>
        <p className="text-xs text-text-dim mt-0.5">
          Deleted jobs and invoices stay here for 30 days, then are purged for good. A job comes back with its
          invoices, payout lines, time entries and expenses, under its original ID.
        </p>
      </div>

      {isLoading ? (
        <div className="text-text-dim text-sm">Loading Trash…</div>
      ) : isError ? (
        <div className="text-sm text-red-accent">
          Couldn't load Trash: {error?.message}{' '}
          <button data-track="trash.retry" onClick={() => refetch()} className="underline hover:text-text-primary">Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-4 bg-surface border border-border rounded text-sm text-text-dim">
          Trash is empty. Deleted jobs and invoices land here for 30 days.
        </div>
      ) : (
        <>
          {open.length === 0 ? (
            <div className="text-sm text-text-dim">Nothing waiting to be restored.</div>
          ) : (
            <div className="space-y-2">{open.map(renderRow)}</div>
          )}
          {done.length > 0 && (
            <div className="space-y-2 pt-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-dim">Restored ({done.length})</h4>
              {done.map(renderRow)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
